import {usePath} from '../contexts/PathContext'
import {useRefInfo} from '../contexts/RefInfoContext'
import {useCurrentRepository} from '@github-ui/current-repository'
import {repoDefaultBrachUrl, repoOverviewUrl} from '@github-ui/paths'
import type {PageError} from '@github-ui/react-core/app-routing-types'
import {AlertIcon, LockIcon} from '@primer/octicons-react'
import {BranchName, Link} from '@primer/react'
import {Blankslate} from '@primer/react/experimental'

import styles from './CodeViewError.module.css'

export function CodeViewError(error: PageError) {
  const repo = useCurrentRepository()
  const path = usePath()
  const refInfo = useRefInfo()

  if (error.httpStatus === 404 && refInfo.currentOid && isWorkflowLockfilePath(path)) {
    return (
      <div className={styles.Box}>
        <Blankslate border>
          <Blankslate.Visual>
            <LockIcon size="medium" />
          </Blankslate.Visual>
          <Blankslate.Heading>No lockfile found</Blankslate.Heading>
          <Blankslate.Description>
            No lockfile was found for the workflows in this repository. Lock your workflow dependencies by running{' '}
            <code>gh actions-lock</code> in your repository.
          </Blankslate.Description>
          <Blankslate.PrimaryAction href={repoOverviewUrl(repo)}>
            Return to the repository overview
          </Blankslate.PrimaryAction>
        </Blankslate>
      </div>
    )
  }

  return (
    <div className={styles.Box}>
      <Blankslate border>
        <Blankslate.Visual>
          <AlertIcon size="medium" />
        </Blankslate.Visual>
        <Blankslate.Heading>
          {error.httpStatus === 404 ? '404 - page not found' : 'Error loading page'}
        </Blankslate.Heading>
        <Blankslate.Description>
          <ErrorDescription {...error} />
        </Blankslate.Description>
        {(path || !refInfo.currentOid) && (
          <Blankslate.PrimaryAction href={refInfo.currentOid ? repoOverviewUrl(repo) : repoDefaultBrachUrl(repo)}>
            {refInfo.currentOid ? 'Return to the repository overview' : 'Go to default branch'}
          </Blankslate.PrimaryAction>
        )}
      </Blankslate>
    </div>
  )
}

function isWorkflowLockfilePath(path: string) {
  return path === '.github/workflows/actions.lock' || path === '.github/workflows-lab/actions.lock'
}

function ErrorDescription({httpStatus, type}: PageError) {
  if (httpStatus === 404) {
    return <DescriptionText404 />
  }
  return <DefaultDescriptionText httpStatus={httpStatus} type={type} />
}

function DescriptionText404() {
  const repo = useCurrentRepository()
  const path = usePath()
  const refInfo = useRefInfo()

  if (!refInfo.currentOid) {
    return (
      <span data-testid="error-404-description">
        Cannot find a valid ref in&nbsp;
        <BranchName as="span">{refInfo.name}</BranchName>
      </span>
    )
  }

  return (
    <span data-testid="error-404-description">
      The&nbsp;
      <BranchName as="span">{refInfo.name}</BranchName>
      &nbsp;branch of&nbsp;
      <span className="text-bold">{repo.name}</span>
      &nbsp;does not contain the path&nbsp;
      <span className="text-bold">{path}.</span>
    </span>
  )
}

function DefaultDescriptionText({httpStatus, type}: {httpStatus?: number; type?: string}) {
  const errorNumberText = httpStatus ? ` ${httpStatus} error` : 'error'

  if (type === 'fetchError') {
    return (
      <span data-testid="fetch-error-description">
        It looks like your internet connection is down. Please check it.
      </span>
    )
  }

  return (
    <span data-testid="default-error-description">
      An unexpected {errorNumberText} occurred. Try
      <Link inline onClick={() => window.location.reload()} key="reload-page">
        &nbsp;reloading the page.
      </Link>
    </span>
  )
}
